import React from 'react';
import PropTypes from 'prop-types';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import moment from 'moment';
import {
  getConvocacaoContagem,
  getNomeArquivoNotificacao,
  getRelatorioJobFromNotificacao,
  isNotificacaoLida,
  isRelatorioDisponivel,
  isRelatorioGerando,
  moduloLabel,
} from '../../../helpers/notificacao';

const CHIP_LABEL = {
  PROCEDIMENTO: 'Procedimento',
  CONVOCACAO: 'Convocação',
  HOLERITE: 'Holerite',
  DOCUMENTO: 'Documento',
  RELATORIO: 'Relatório',
};

const formatWhen = (value) => {
  if (!value) return '';
  const m = moment(value);
  return m.isValid() ? m.fromNow() : '';
};

const formatDate = (value) => {
  if (!value) return '';
  const m = moment(value);
  return m.isValid() ? m.format('DD/MM/YYYY') : '';
};

const prazoDocumento = (item) => {
  const metadata = item.metadata || {};
  const evento = item.evento || '';
  if (evento === 'DOCUMENTO_VENCIDO') {
    const data = formatDate(metadata.dataVencimento || metadata.vencimentoEm || metadata.vencimento);
    return data ? `Venceu em ${data}` : 'Vencido';
  }
  const diasRaw = metadata.diasRestantes != null ? metadata.diasRestantes : metadata.dias;
  if (diasRaw != null && diasRaw !== '') {
    const dias = Number(diasRaw);
    if (dias === 0) return 'Vence hoje';
    if (dias === 1) return 'Vence em 1 dia';
    if (Number.isFinite(dias) && dias > 0) return `Vence em ${dias} dias`;
  }
  const titulo = String(item.titulo || '');
  const match = titulo.match(/(\d+)\s+dia/i);
  if (match && /venc/i.test(titulo)) {
    const dias = Number(match[1]);
    if (dias === 1) return 'Vence em 1 dia';
    return `Vence em ${dias} dias`;
  }
  if (/vencid/i.test(titulo)) return 'Vencido';
  return '';
};

const horarioLinha = (item, entregueEm) => {
  if (item.modulo === 'DOCUMENTO') {
    const prazo = prazoDocumento(item);
    if (prazo) return prazo;
  }
  return formatWhen(entregueEm);
};

const countChipSx = (tone) => ({
  fontSize: 11.5,
  fontWeight: 700,
  borderRadius: 999,
  px: 1,
  py: 0.25,
  color: tone,
  border: 1,
  borderColor: 'divider',
});

const fileSx = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  flexShrink: 0,
  alignSelf: 'flex-start',
  maxWidth: 240,
  px: 1.25,
  py: 0.75,
  border: 1,
  borderColor: 'divider',
  borderRadius: 1,
};

const NotificacaoItem = ({
  item,
  showMessage,
  onDownload,
  downloading,
  onOpen,
  showNavigate,
}) => {
  const unread = !isNotificacaoLida(item);
  const contagem = getConvocacaoContagem(item);
  const gerando = isRelatorioGerando(item);
  const job = isRelatorioDisponivel(item) ? getRelatorioJobFromNotificacao(item) : null;
  const nomeArquivo = getNomeArquivoNotificacao(item);
  const entregueEm = item.entregueEm || item.criadoEm;
  const modulo = CHIP_LABEL[item.modulo] || moduloLabel(item.modulo);
  const horario = horarioLinha(item, entregueEm);
  const hasFileCard = gerando || (job && onDownload);

  const openFromKeyboard = (event) => {
    if (!onOpen) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onOpen();
    }
  };

  return (
    <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', width: '100%', minWidth: 0 }}>
      <Box
        role={onOpen ? 'button' : undefined}
        tabIndex={onOpen ? 0 : undefined}
        aria-label={onOpen ? (item.titulo || 'Abrir notificação') : undefined}
        onClick={onOpen || undefined}
        onKeyDown={openFromKeyboard}
        sx={{
          display: 'flex',
          flex: 1,
          minWidth: 0,
          gap: 1,
          alignItems: 'flex-start',
          textAlign: 'left',
          cursor: onOpen ? 'pointer' : 'default',
          borderRadius: 1,
          outline: 'none',
          '&:hover': onOpen ? { bgcolor: 'action.hover' } : undefined,
          '&:focus-visible': {
            boxShadow: (theme) => `0 0 0 2px ${theme.palette.primary.main}`,
          },
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            {unread && (
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: 'error.main',
                  flexShrink: 0,
                }}
              />
            )}
            <Typography
              variant="body2"
              noWrap
              sx={{ fontWeight: unread ? 700 : 500 }}
            >
              {item.titulo}
            </Typography>
          </Box>
          {showMessage && item.mensagem && !contagem && (
            <Typography
              variant="caption"
              color="text.secondary"
              noWrap
              display="block"
            >
              {item.mensagem}
            </Typography>
          )}
          {contagem && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 0.5 }}>
              <Box component="span" sx={countChipSx('success.main')}>
                {`${contagem.aceitas} aceitaram`}
              </Box>
              <Box component="span" sx={countChipSx('error.main')}>
                {`${contagem.negadas} negaram`}
              </Box>
              <Box component="span" sx={countChipSx('warning.main')}>
                {`${contagem.expiradas} não responderam`}
              </Box>
            </Box>
          )}
          {(modulo || horario) && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5, minWidth: 0 }}>
              {modulo && (
                <Box
                  component="span"
                  sx={{
                    flexShrink: 0,
                    fontSize: 11,
                    lineHeight: '16px',
                    px: 0.75,
                    py: 0.25,
                    borderRadius: 0.5,
                    border: 1,
                    borderColor: 'divider',
                    color: 'text.secondary',
                    bgcolor: 'action.hover',
                  }}
                >
                  {modulo}
                </Box>
              )}
              {horario && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  noWrap
                >
                  {horario}
                </Typography>
              )}
            </Box>
          )}
        </Box>
        {showNavigate && (
          <ChevronRightIcon sx={{ color: 'text.secondary', flexShrink: 0, mt: 0.25 }} />
        )}
      </Box>

      {hasFileCard && gerando && (
        <Box
          onClick={(event) => event.stopPropagation()}
          onMouseDown={(event) => event.stopPropagation()}
          sx={fileSx}
        >
          <DescriptionOutlinedIcon sx={{ fontSize: 18, color: 'warning.main' }} />
          <Typography variant="caption" noWrap sx={{ fontWeight: 600 }}>
            {nomeArquivo || 'Gerando documento…'}
          </Typography>
          <CircularProgress size={16} sx={{ color: 'warning.main', flexShrink: 0 }} />
        </Box>
      )}

      {hasFileCard && job && onDownload && (
        <Box
          onClick={(event) => event.stopPropagation()}
          onMouseDown={(event) => event.stopPropagation()}
          sx={fileSx}
        >
          <DescriptionOutlinedIcon sx={{ fontSize: 18, color: 'info.main' }} />
          <Typography variant="caption" noWrap sx={{ flex: 1, minWidth: 0, fontWeight: 600 }}>
            {nomeArquivo || 'Documento Word'}
          </Typography>
          <Button
            size="small"
            variant="outlined"
            disabled={downloading}
            onClick={(event) => {
              event.stopPropagation();
              onDownload(job);
            }}
            startIcon={downloading ? (
              <CircularProgress size={14} color="inherit" />
            ) : undefined}
            sx={{ flexShrink: 0 }}
          >
            {downloading ? 'Baixando...' : 'Baixar'}
          </Button>
        </Box>
      )}
    </Box>
  );
};

NotificacaoItem.propTypes = {
  item: PropTypes.shape({
    modulo: PropTypes.string,
    lida: PropTypes.bool,
    titulo: PropTypes.string,
    mensagem: PropTypes.string,
    criadoEm: PropTypes.string,
    entregueEm: PropTypes.string,
    visualizadaEm: PropTypes.string,
    dataLeitura: PropTypes.string,
    evento: PropTypes.string,
    metadata: PropTypes.object,
  }).isRequired,
  showMessage: PropTypes.bool,
  onDownload: PropTypes.func,
  downloading: PropTypes.bool,
  onOpen: PropTypes.func,
  showNavigate: PropTypes.bool,
};

NotificacaoItem.defaultProps = {
  showMessage: false,
  onDownload: null,
  downloading: false,
  onOpen: null,
  showNavigate: false,
};

export default NotificacaoItem;
